import { useId } from "@reach/auto-id";
import { ComponentProps, FC, SVGProps, forwardRef } from "react";

import { FieldLabel } from "@theme/components/Form";
import {
  Box,
  Input,
  Label,
  Text,
  ThemeUIStyleObject,
  getThemeValue,
} from "@theme/components/ThemeUi";
import { markTextAsRequired } from "@theme/utils";

export interface TextFieldProps extends ComponentProps<"input"> {
  name: string;
  label: string;
  labelPosition?: "above" | "inside";
  error?: string | boolean;
  helper?: string;
  /**
   * If an Icon is directly passed in, it will be rendered
   * or Pass in a function to render a IconButton with onclick handlers
   * See https://storybook.manscaped.io/?path=/docs/components-forms--text-area-field-with-icon-button
   */
  LeadingIcon?: FC<SVGProps<SVGElement>> | (() => JSX.Element);
  /**
   * If an Icon is directly passed in, it will be rendered
   * or Pass in a function to render a IconButton with onclick handlers
   * https://storybook.manscaped.io/?path=/docs/components-forms--text-area-field-with-icon-button
   */
  TrailingIcon?: FC<SVGProps<SVGElement>> | (() => JSX.Element);
  sx?: ThemeUIStyleObject;
  className?: string;
  placeholder?: string;
  "data-testid"?: string;
  "data-error"?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    sx,
    id,
    name,
    label,
    labelPosition = "inside",
    required = false,
    disabled = false,
    error,
    helper,
    LeadingIcon,
    TrailingIcon,
    className,
    placeholder,
    "data-error": dataError,
    ...rest
  },
  ref,
) {
  const autoId = useId(id);
  const longLabel = label.length > 35;

  const textLabel = markTextAsRequired(label, required);

  const textLabelPosition = longLabel ? "above" : labelPosition;

  return (
    <Box sx={{ position: "relative", ...sx }} className={className}>
      {textLabelPosition === "above" && (
        <FieldLabel label={textLabel} error={error} data-testid="text-field-label" />
      )}
      <Label
        htmlFor={autoId}
        sx={{
          zIndex: 1,
          position: "relative",
          height: 50,
          variant: "forms.fieldBorder",
          ...(disabled && {
            "&:hover:after": {
              borderColor: "borderLevel2",
            },
          }),
          ...(error && {
            "&:after, &:hover:after": {
              borderWidth: "default",
              borderColor: "borderError",
            },
          }),
        }}
      >
        {LeadingIcon && (
          <LeadingIcon
            data-role="leadingIcon"
            sx={{
              variant: "forms.leadingIcon",
              color: disabled ? "interactiveNeutralStrong" : "textPrimary",
            }}
          />
        )}

        <Input
          {...rest}
          ref={ref}
          id={autoId}
          name={name}
          placeholder={placeholder ?? ""}
          disabled={disabled}
          aria-describedby={`${autoId}_error_text`}
          required={required}
          sx={{
            variant: "forms.field",
            cursor: disabled ? "not-allowed" : "text",
            ...(textLabelPosition === "inside" && { pt: "s500", pb: "s200" }),
            pl: (t) =>
              LeadingIcon
                ? `calc(${getThemeValue(t, "space.s800")} + ${getThemeValue(t, "space.s300")})`
                : "s400",
            pr: TrailingIcon ? "s800" : "s400",
            bg: disabled ? "interactiveNeutralDim" : "transparent",
            ...(error && { bg: "backgroundError" }),
            // This is confusing so noting for future - When focused move the label up.
            // Having placeholder ?? "" gives this always a placeholder-shown state until you type in the input.
            // So this triggers whenever you type, then placeholder-shown vanishes and this condition hits.
            "&:focus, &:not(:placeholder-shown)": {
              outline: "none",
              '+ div[data-role="label"]': {
                top: 0,
                transform: (t) => `translateY(${getThemeValue(t, "space.s200")})`,
              },
            },
          }}
        />

        {textLabelPosition === "inside" && (
          <FieldLabel
            label={textLabel}
            error={error}
            data-testid="text-field-label"
            sx={{
              height: "min-content",
              left: "s400",
              position: "absolute",
              mb: 0,
              // This checks the actual value of the placeholder. If it exists, move the label up. Otherwise, center.
              ...(placeholder
                ? { transform: (t) => `translateY(${getThemeValue(t, "space.s200")})`, top: 0 }
                : { transform: "translateY(-50%)", top: "50%" }),
              ...(LeadingIcon && {
                left: (t) =>
                  `calc(${getThemeValue(t, "space.s800")} + ${getThemeValue(t, "space.s300")})`,
              }),
            }}
          />
        )}

        {TrailingIcon && (
          <TrailingIcon
            data-role="trailingIcon"
            sx={{
              variant: "forms.trailingIcon",
              color: disabled ? "interactiveNeutralStrong" : "textPrimary",
            }}
          />
        )}
      </Label>
      <Box aria-live="polite">
        {error && typeof error === "string" && (
          <Text
            id={`${autoId}_error_text`}
            as="small"
            variant="errorText"
            role="alert"
            data-testid="text-field-error"
            data-error={dataError}
          >
            {error}
          </Text>
        )}
      </Box>

      {!error && helper && (
        <Text as="small" variant="helperText" aria-live="polite">
          {helper}
        </Text>
      )}
    </Box>
  );
});
